import React from 'react';
import Inteligencia from '../pages/gerenciaGeneral/Inteligencia';
import Investigaciones from '../pages/gerenciaGeneral/Investigaciones/Investigaciones';
import Operaciones from '../pages/gerenciaGeneral/Operaciones';
import ReportesOperativos from '../components/ReportesOperativos/Main';
import PanelGerencia from '../pages/gerenciaGeneral/PanelGerencia';
import { getCurrentConfig, getComponentByAdvancedRules } from '../config/dynamicRoutesConfig';

/**
 * Mapeo de acciones a componentes
 * Define qué componente renderizar según la acción en la URL o especificación explícita
 */
const COMPONENT_MAPPING = {
    // Mapeo por acción (última parte de la URL)
    'solicitar': Inteligencia,
    'administrar': Investigaciones,
    'procesos': ReportesOperativos,
    'reportes': Operaciones,
    'reportes_operativos': ReportesOperativos,
    'panel': PanelGerencia,
    'default': PanelGerencia,
};

/**
 * Extrae todas las rutas únicas del menú junto con sus componentes explícitos
 * @param {Object} menuData - Datos del menú desde Redux
 * @returns {Array} Array de objetos con ruta y componente (si está especificado)
 */
const extractRoutesFromMenu = (menuData) => {
    const routesMap = new Map();
    
    if (!menuData || !menuData.menu) {
        return [];
    }
    
    const traverseMenuItems = (items) => {
        items.forEach(item => {
            // Si el item tiene un link, lo agregamos
            if (item.link && typeof item.link === 'string') {
                const routeInfo = {
                    path: item.link,
                    explicitComponent: item.component || null, // 🎯 Componente explícito del menú
                    menuItem: item // Para debug y referencia
                };
                
                routesMap.set(item.link, routeInfo);
            }
            
            // Si tiene subItems, los recorremos recursivamente
            if (item.subItems && Array.isArray(item.subItems)) {
                traverseMenuItems(item.subItems);
            }
        });
    };
    
    // Recorrer todas las secciones del menú
    menuData.menu.forEach(section => {
        if (section.items && Array.isArray(section.items)) {
            traverseMenuItems(section.items);
        }
    });
    
    return Array.from(routesMap.values());
};

/**
 * Determina qué componente usar basado en la ruta y configuración del menú
 * @param {string} path - Ruta a analizar
 * @param {string|null} explicitComponent - Componente explícito definido en el menú
 * @returns {React.Component} Componente a renderizar
 */
const determineComponentFromPath = (path, explicitComponent = null) => {
    const config = getCurrentConfig();
    
    // 🎯 PRIORIDAD 1: Componente explícito del menú
    if (explicitComponent && COMPONENT_MAPPING[explicitComponent]) {
        return COMPONENT_MAPPING[explicitComponent];
    }
    
    // PRIORIDAD 2: Reglas avanzadas
    const advancedComponent = getComponentByAdvancedRules(path);
    if (advancedComponent && COMPONENT_MAPPING[advancedComponent]) {
        return COMPONENT_MAPPING[advancedComponent];
    }
    
    // PRIORIDAD 3: Mapeo personalizado
    const customMapping = config.customComponentMapping;
    const pathParts = path.split('/').filter(part => part.length > 0);
    const lastPart = pathParts[pathParts.length - 1];
    
    if (customMapping[lastPart]) {
        const customComponent = COMPONENT_MAPPING[customMapping[lastPart]];
        if (customComponent) {
            return customComponent;
        }
    }
    
    // PRIORIDAD 4: Mapeo estándar por última parte de la URL
    const component = COMPONENT_MAPPING[lastPart] || COMPONENT_MAPPING['default'];
    
    return component;
};

/**
 * Genera rutas dinámicas basadas en el menú
 * @param {Object} menuData - Datos del menú
 * @param {Array} existingRoutes - Rutas que ya existen (para evitar duplicados)
 * @returns {Array} Array de objetos de ruta para React Router
 */
export const generateDynamicRoutes = (menuData, existingRoutes = []) => {
    const config = getCurrentConfig();
    
    // Medir performance si está habilitado
    const startTime = config.performance.measurePerformance ? performance.now() : 0;
    
    const menuRoutes = extractRoutesFromMenu(menuData);
    const dynamicRoutes = [];
    
    // Filtrar rutas que no existen ya en el sistema
    const newRoutes = menuRoutes.filter(routeInfo => {
        // Verificar patrones a ignorar
        const shouldIgnore = config.ignorePatterns.some(pattern => 
            routeInfo.path.includes(pattern)
        );
        
        return !shouldIgnore && !existingRoutes.includes(routeInfo.path);
    });
    
    // Limitar número de rutas si está configurado
    const routesToProcess = config.performance.maxDynamicRoutes 
        ? newRoutes.slice(0, config.performance.maxDynamicRoutes)
        : newRoutes;
    
    // Generar objetos de ruta para React Router
    routesToProcess.forEach(routeInfo => {
        const { path, explicitComponent } = routeInfo;
        
        const component = determineComponentFromPath(path, explicitComponent);
        
        const routeConfig = {
            path,
            component,
            exact: true,
            key: `${config.routeKeyPrefix}${path}`,
            isDynamic: true,
            explicitComponent, // Para debug
            source: explicitComponent ? 'explicit' : 'rules' // Para debug
        };
        
        dynamicRoutes.push(routeConfig);
    });
    
    return dynamicRoutes;
};

/**
 * Componente wrapper para rutas dinámicas
 * Permite pasar props adicionales y manejar parámetros de ruta
 */
export const DynamicRouteWrapper = ({ component: Component, ...props }) => {
    return <Component {...props} />;
};

/**
 * Obtiene las rutas existentes del sistema actual
 * Esta función ayuda a evitar duplicados
 */
export const getExistingRoutes = () => {
    return [
        "/",
        "/home",
        "/dashboard-presidencia",
        "/dashboard-gerencia",
        "/Notifications",
        "/perfil",
        "/presidencia/administrativo/solicitar",
        "/presidencia/administrativo/administrar",
        "/presidencia/administrativo/reportes",
        "/presidencia/administrativo/reportes_operativos",
        "/presidencia/administrativo/procesos",
        "/presidencia/ayf/cartera/solicitar",
        "/presidencia/ayf/cartera/administrar",
        "/presidencia/ayf/cartera/reportes",
        "/presidencia/ayf/cartera/reportes_operativos",
        "/presidencia/ayf/cartera/procesos",
        "/presidencia",
        "/presidencia/ayf/compras/solicitar",
        "/presidencia/ayf/compras/administrar",
        "/presidencia/ayf/compras/reportes",
        "/presidencia/ayf/compras/reportes_operativos",
        "/presidencia/ayf/compras/procesos",
        "/presidencia/ayf/contabilidad/solicitar",
        "/presidencia/ayf/contabilidad/administrar",
        "/presidencia/ayf/contabilidad/reportes",
        "/presidencia/ayf/contabilidad/reportes_operativos",
        "/presidencia/ayf/contabilidad/procesos",
        "/presidencia/ayf/facturacion/solicitar",
        "/presidencia/ayf/facturacion/administrar",
        "/presidencia/ayf/facturacion/reportes",
        "/presidencia/ayf/facturacion/reportes_operativos",
        "/presidencia/ayf/facturacion/procesos",
        "/presidencia/ayf/servicios-generales/solicitar",
        "/presidencia/ayf/servicios-generales/administrar",
        "/presidencia/ayf/servicios-generales/reportes",
        "/presidencia/ayf/servicios-generales/reportes_operativos",
        "/presidencia/ayf/servicios-generales/procesos",
        "/presidencia/ayf/SGI/solicitar",
        "/presidencia/ayf/SGI/administrar",
        "/presidencia/ayf/SGI/reportes",
        "/presidencia/ayf/SGI/reportes_operativos",
        "/presidencia/ayf/SGI/procesos",
        "/presidencia/comercial/gestionComercial/solicitar",
        "/presidencia/comercial/gestionComercial/administrar",
        "/presidencia/comercial/gestionComercial/reportes",
        "/presidencia/comercial/gestionComercial/reportes_operativos",
        "/presidencia/comercial/gestionComercial/procesos",
        "/presidencia/comunicaciones/solicitar",
        "/presidencia/comunicaciones/administrar",
        "/presidencia/comunicaciones/reportes",
        "/presidencia/comunicaciones/reportes_operativos",
        "/presidencia/comunicaciones/procesos",
        "/presidencia/encuestas/solicitar",
        "/presidencia/encuestas/administrar",
        "/presidencia/encuestas/reportes",
        "/presidencia/encuestas/reportes_operativos",
        "/presidencia/encuestas/procesos",
        "/presidencia/gestionHumana/bienestarYdesarrollo/solicitar",
        "/presidencia/gestionHumana/bienestarYdesarrollo/administrar",
        "/presidencia/gestionHumana/bienestarYdesarrollo/reportes",
        "/presidencia/gestionHumana/bienestarYdesarrollo/reportes_operativos",
        "/presidencia/gestionHumana/bienestarYdesarrollo/procesos",
        "/presidencia/gestionHumana/capacitacion/solicitar",
        "/presidencia/gestionHumana/capacitacion/administrar",
        "/presidencia/gestionHumana/capacitacion/reportes",
        "/presidencia/gestionHumana/capacitacion/reportes_operativos",
        "/presidencia/gestionHumana/capacitacion/procesos",
        "/presidencia/gestionHumana/contratacionYdesvinculacion/solicitar",
        "/presidencia/gestionHumana/contratacionYdesvinculacion/administrar",
        "/presidencia/gestionHumana/contratacionYdesvinculacion/reportes",
        "/presidencia/gestionHumana/contratacionYdesvinculacion/reportes_operativos",
        "/presidencia/gestionHumana/contratacionYdesvinculacion/procesos",
        "/presidencia/gestionHumana/nominaYseguridadSocial/solicitar",
        "/presidencia/gestionHumana/nominaYseguridadSocial/administrar",
        "/presidencia/gestionHumana/nominaYseguridadSocial/reportes",
        "/presidencia/gestionHumana/nominaYseguridadSocial/reportes_operativos",
        "/presidencia/gestionHumana/nominaYseguridadSocial/procesos",
        "/presidencia/gestionHumana/reclutamiento/solicitar",
        "/presidencia/gestionHumana/reclutamiento/administrar",
        "/presidencia/gestionHumana/reclutamiento/reportes",
        "/presidencia/gestionHumana/reclutamiento/reportes_operativos",
        "/presidencia/gestionHumana/reclutamiento/procesos",
        "/presidencia/gestionHumana/SSTyAmbiental/solicitar",
        "/presidencia/gestionHumana/SSTyAmbiental/administrar",
        "/presidencia/gestionHumana/SSTyAmbiental/reportes",
        "/presidencia/gestionHumana/SSTyAmbiental/reportes_operativos",
        "/presidencia/gestionHumana/SSTyAmbiental/procesos",
        "/presidencia/gestionHumana/gestion/solicitar",
        "/presidencia/gestionHumana/gestion/administrar",
        "/presidencia/gestionHumana/gestion/reportes",
        "/presidencia/gestionHumana/gestion/reportes_operativos",
        "/presidencia/gestionHumana/gestion/procesos",
        "/presidencia/gestionHumana/sgsst/solicitar",
        "/presidencia/gestionHumana/sgsst/administrar",
        "/presidencia/gestionHumana/sgsst/reportes",
        "/presidencia/gestionHumana/sgsst/reportes_operativos",
        "/presidencia/gestionHumana/sgsst/procesos",
        "/presidencia/juridico/solicitar",
        "/presidencia/juridico/administrar",
        "/presidencia/juridico/reportes",
        "/presidencia/juridico/reportes_operativos",
        "/presidencia/juridico/procesos",
        "/presidencia/juridico/procesos-administrativos/solicitar",
        "/presidencia/juridico/procesos-administrativos/administrar",
        "/presidencia/juridico/procesos-administrativos/reportes",
        "/presidencia/juridico/procesos-administrativos/reportes_operativos",
        "/presidencia/juridico/procesos-administrativos/procesos",
        "/presidencia/juridico/procesos-civiles/solicitar",
        "/presidencia/juridico/procesos-civiles/administrar",
        "/presidencia/juridico/procesos-civiles/reportes",
        "/presidencia/juridico/procesos-civiles/reportes_operativos",
        "/presidencia/juridico/procesos-civiles/procesos",
        "/presidencia/juridico/procesos-comerciales/solicitar",
        "/presidencia/juridico/procesos-comerciales/administrar",
        "/presidencia/juridico/procesos-comerciales/reportes",
        "/presidencia/juridico/procesos-comerciales/reportes_operativos",
        "/presidencia/juridico/procesos-comerciales/procesos",
        "/presidencia/juridico/procesos-laborales/solicitar",
        "/presidencia/juridico/procesos-laborales/administrar",
        "/presidencia/juridico/procesos-laborales/reportes",
        "/presidencia/juridico/procesos-laborales/reportes_operativos",
        "/presidencia/juridico/procesos-laborales/procesos",
        "/presidencia/TI/solicitar",
        "/presidencia/TI/administrar",
        "/presidencia/TI/reportes",
        "/presidencia/TI/reportes_operativos",
        "/presidencia/TI/procesos",
        "/panel_panorama/:id",
        "/gerencia/auditorias/panorama_riesgos/:id/gestionar",
        "/gerencia/auditorias/panorama_riesgos/informe_:id",
        "/gerencia/auditorias/solicitar",
        "/gerencia/auditorias/administrar",
        "/gerencia/auditorias/reportes",
        "/gerencia/auditorias/reportes_operativos",
        "/gerencia/auditorias/procesos",
        "/gerencia/inteligencia/solicitar",
        "/gerencia/inteligencia/administrar",
        "/gerencia/inteligencia/administrar/:variable",
        "/gerencia/inteligencia/administrar/:id/:tipoReporte",
        "/gerencia/inteligencia/reportes",
        "/gerencia/inteligencia/reportes_operativos",
        "/gerencia/inteligencia/solicitar/creada/:id",
        "/gerencia/inteligencia/reportes/:id",
        "/gerencia/inteligencia/editar/:id",
        "/gerencia/inteligencia/procesos",
        "/gerencia/operaciones",
        "/gerencia/investigaciones/solicitar",
        "/gerencia/investigaciones/administrar",
        "/gerencia/investigaciones/administrar/:variable",
        "/gerencia/investigaciones/reportes",
        "/gerencia/investigaciones/reportes_operativos",
        "/gerencia/investigaciones/solicitar/creada/:id",
        "/gerencia/investigaciones/procesos",
        "/gerencia/investigaciones-administrativas/solicitar",
        "/gerencia/investigaciones-administrativas/solicitar/creada/:id",
        "/gerencia/investigaciones-administrativas/administrar",
        "/gerencia/investigaciones-administrativas/administrar/:id/:tipoReporte",
        "/gerencia/investigaciones-administrativas/administrar/:idSolicitud/editar_registro/",
        "/gerencia/investigaciones-administrativas/reportes",
        "/gerencia/investigaciones-administrativas/reportes_operativos",
        "/gerencia/investigaciones-administrativas/busqueda",
        "/gerencia/investigaciones-administrativas/reportes/:id",
        "/gerencia/investigaciones-administrativas/procesos",
        "/presidencia/reportes",
        "/gerencia/operaciones/solicitar",
        "/gerencia/operaciones/administrar",
        "/gerencia/operaciones/reportes",
        "/gerencia/operaciones/reportes_operativos",
        "/gerencia/operaciones/procesos",
        "/gerencia/operaciones/solicitar/creada/:id",
        "/gerencia/auditorias/solicitar/creada/:id",
        "/gerencia/investigaciones/creada/:id",
        "/gerencia/:seccion/gestor/creada/:id",
        "/gerencia/:seccion/:subseccion/gestor/creada/:id",
        "/presidencia/:seccion/gestor/creada/:id",
        "/presidencia/:seccion/:subseccion/gestor/creada/:id",
        "/dashboard-gerencia/:departamento/detalle_departamento/",
        "/dashboard-gerencia/:departamento/detalle_departamento_panel/",
        "/pdf/",
        "/reportes-operativos/",
        "/prueba_mapa/",
        "/cargue-masivo/",
        "/descargue/"
    ];
};

/**
 * Función de debug para mostrar información sobre las rutas generadas
 */
export const debugDynamicRoutes = (menuData, existingRoutes) => {
    console.group('🚀 Dynamic Routes Debug');
    
    const menuRoutes = extractRoutesFromMenu(menuData);
    const dynamicRoutes = generateDynamicRoutes(menuData, existingRoutes);
    
    console.log('📋 Rutas extraídas del menú:', menuRoutes.length);
    console.log('⚡ Rutas dinámicas generadas:', dynamicRoutes.length);
    
    // Mostrar detalles de componentes explícitos
    const explicitRoutes = dynamicRoutes.filter(r => r.explicitComponent);
    const ruleBasedRoutes = dynamicRoutes.filter(r => !r.explicitComponent);
    
    console.log(`🎯 Rutas con componente explícito: ${explicitRoutes.length}`);
    console.log(`📏 Rutas basadas en reglas: ${ruleBasedRoutes.length}`);
    
    if (explicitRoutes.length > 0) {
        console.group('🎯 Componentes Explícitos:');
        explicitRoutes.forEach(route => {
            console.log(`  ${route.path} → ${route.explicitComponent}`);
        });
        console.groupEnd();
    }
    
    if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Todas las rutas dinámicas:', dynamicRoutes.map(r => ({
            path: r.path,
            component: r.component.name,
            source: r.source,
            explicit: r.explicitComponent
        })));
    }
    
    console.groupEnd();
    
    return dynamicRoutes;
};
